import { CloseOutlined } from '@ant-design/icons';
import { validate } from 'email-validator';
import { useMemo } from 'react';
import styled from 'styled-components';

import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import { AttrTextInput } from '@/components/Inputs';
import FlexibleTitleInput from '@/components/Inputs/FlexibleTitle';
import type { SelectOptionT } from '@/components/Select/types';
import { AttrText } from '@/components/Typography/NamedTypo';
import useAgentStore from '@/features/Agent/stores';
import AttrSelectDropdown from '@/features/Commons/Dropdown/AttrSelectDropdown';
import useKeymap from '@/hooks/pure/useKeymap';
import useStates from '@/hooks/pure/useStates';
import useToastStore from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { detailModalStyle } from '@/styles/style';

type State = {
  name: string;
  email: string;
  role: string | null;
  team: string | null;
};

const CreateAgentModal = () => {
  const { addNewAgent, setState, fetchAgentList, agentMap } = useAgentStore(
    (state) => ({
      addNewAgent: state.addNewAgent,
      setState: state.setState,
      agentMap: state.agentMap,
      fetchAgentList: state.fetchAgentList,
    }),
  );

  const { workspaceFieldOptions, updateWorkspaceFieldOptions } =
    useWorkspaceStore((state) => {
      return {
        workspaceFieldOptions: state.workspaceFieldOptions,
        updateWorkspaceFieldOptions: state.updateWorkspaceFieldOptions,
      };
    });

  const roleOptions: SelectOptionT[] = useMemo(() => {
    return workspaceFieldOptions.role_values.map((value) => ({
      value,
      searchName: value,
    }));
  }, [workspaceFieldOptions.role_values]);

  const teamOptions: SelectOptionT[] = useMemo(() => {
    return workspaceFieldOptions.team_values.map((value) => ({
      value,
      searchName: value,
    }));
  }, [workspaceFieldOptions.team_values]);

  const { addDefaultToast } = useToastStore((state) => ({
    addDefaultToast: state.addDefaultToast,
  }));

  const { state, setValue } = useStates<State>({
    name: '',
    email: '',
    role: null,
    team: null,
  });

  const closeModal = () => {
    setState({ createAgentModalStatus: false });
  };

  const onSave = async () => {
    try {
      if (state.name === '') {
        addDefaultToast('Name must be filled', 'fail');
        return;
      }
      for (const agent of Object.values(agentMap)) {
        if (state.email && agent.email?.includes(state.email)) {
          addDefaultToast('This email already exists.', 'fail');
          return;
        }
      }

      if (state.email && !validate(state.email)) {
        addDefaultToast('Invalid email format.', 'fail');
        return;
      }
      const req: PostAgentParams = {
        name: state.name,
        email: state.email,
        role: state.role || undefined,
        team: state.team || undefined,
      };

      await addNewAgent(req);
      fetchAgentList();
      setState({ createAgentModalStatus: false });
    } catch (e) {
      console.error(e);
      addDefaultToast('Create fail', 'fail');
    }
  };

  useKeymap('Escape', closeModal);

  return (
    <ModalStyle status={true}>
      <ContentStyle>
        <Flex j="flex-end">
          <IconButton
            icon={<CloseOutlined />}
            onClick={closeModal}
            tooltip="Close"
            shortcut="ESC"
          />
        </Flex>

        <Space h="5px" />
        <FlexibleTitleInput
          placeholder="New Agent name"
          name="name"
          value={state.name}
          onChange={(e) => setValue('name', e.target.value)}
        />
        <Space h="21px" />
        <VFlex g="5px">
          <Flex g="10px" a="center" className="property-row">
            <AttrText className="property-label">Email address</AttrText>
            <AttrTextInput
              placeholder="Enter email address"
              name="email"
              value={state.email}
              onChange={(e) => setValue('email', e.target.value)}
            />
          </Flex>
          <Flex g="10px" className="property-row">
            <AttrText className="property-label">Role</AttrText>
            <AttrSelectDropdown
              name={`agent-create-role-dropdown`}
              view="create"
              selected={state.role || null}
              options={roleOptions}
              onSelect={(key) => setValue('role', key)}
              onClear={() => setValue('role', null)}
              onCreate={async (key) => {
                await updateWorkspaceFieldOptions(1, 'role', key);
                setValue('role', key);
              }}
              onDelete={(key) =>
                updateWorkspaceFieldOptions(4, 'role', key.value as string)
              }
            />
          </Flex>
          <Flex g="10px" className="property-row">
            <AttrText className="property-label">Team</AttrText>
            <AttrSelectDropdown
              name={`agent-create-team-dropdown`}
              view="create"
              selected={state.team || null}
              options={teamOptions}
              onSelect={(key) => setValue('team', key)}
              onClear={() => setValue('team', null)}
              onCreate={async (key) => {
                await updateWorkspaceFieldOptions(1, 'team', key);
                setValue('team', key);
              }}
              onDelete={(key) =>
                updateWorkspaceFieldOptions(4, 'team', key.value as string)
              }
            />
          </Flex>
        </VFlex>
      </ContentStyle>
      <Space h="22px" />
      <Flex j="flex-end" style={{ marginRight: '32px' }}>
        <Button onClick={onSave}>Save</Button>
      </Flex>
    </ModalStyle>
  );
};

export default CreateAgentModal;

const ModalStyle = styled(FadeDiv)`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: 0;
  ${detailModalStyle}
`;

const ContentStyle = styled.div`
  padding: 28px 32px 0 32px;

  .property-row {
    gap: 10px;
    align-items: center;
    height: 32px;
  }

  .property-label {
    width: 120px;
    height: 24px;
    line-height: 24px;
  }
`;
